/**
 * Profils et droits — refonte UX, chantier 4 (maquette 10, validée le 24/09).
 *
 * « Qui fait quoi » : chaque personne a un profil MODÈLE (Administrateur complet,
 * Maire, Adjoint délégué), ajustable service par service : Aucun / Lecture / Gestion.
 * Le maire décide, la DGS administre (CGCT L2122-18 et L2122-19). Il reste toujours
 * au moins un administrateur complet (invariant M21, vérifié par le serveur).
 * Seul un administrateur complet modifie les profils (le serveur le garantit).
 */
import { useEffect, useMemo, useState } from 'react';
import { Gavel, Eye, ShieldCheck } from 'lucide-react';
import { Card, Button, Badge, Input } from '@/components/ui';
import { api, ApiException, type ProfilGouv, type ProfilGouvDTO, type BriqueGouv } from '@/lib/api';
import { RepriseMairePanel } from '@/components/admin/gouvernance/RepriseMairePanel';

const BRIQUES: Array<{ code: BriqueGouv; libelle: string }> = [
  { code: 'service_technique', libelle: 'Service technique' }, { code: 'grc', libelle: 'Relation citoyens' },
  { code: 'scolaire_cantine', libelle: 'Scolaire et cantine' }, { code: 'petite_enfance', libelle: 'Petite enfance' },
  { code: 'urbanisme', libelle: 'Urbanisme' }, { code: 'comptable', libelle: 'Comptabilité' },
  { code: 'rh', libelle: 'Ressources humaines' }, { code: 'associations', libelle: 'Associations' },
  { code: 'agents', libelle: 'Annuaire des agents' },
];
const LIB_PROFIL: Record<ProfilGouv, string> = { administrateur: 'Administrateur complet', maire: 'Maire', adjoint_delegue: 'Adjoint délégué' };
type Niveau = 'aucun' | 'lecture' | 'gestion';

function badgeProfil(p: ProfilGouvDTO) {
  if (p.adminComplet) return <Badge variant="success" size="sm">Administrateur complet</Badge>;
  if (p.profil === 'maire') return <Badge variant="info" size="sm">Maire</Badge>;
  if (p.profil === 'adjoint_delegue') return <Badge variant="neutral" size="sm">Adjoint délégué</Badge>;
  if (p.role === 'admin') return <Badge variant="warning" size="sm">Admin de service</Badge>;
  return <Badge variant="neutral" size="sm">Manager</Badge>;
}

export function ProfilsDroitsPage() {
  const [profils, setProfils] = useState<ProfilGouvDTO[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  const [profil, setProfil] = useState<ProfilGouv>('maire');
  const [niveaux, setNiveaux] = useState<Record<BriqueGouv, Niveau>>({} as Record<BriqueGouv, Niveau>);
  const [ref, setRef] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null);

  const charger = () => api.gouvernance.profils().then((r) => setProfils(r.profils)).catch(() => setProfils([]));
  useEffect(() => { void charger(); }, []);
  const sel = useMemo(() => profils.find((p) => p.id === selId) ?? null, [profils, selId]);

  function modele(p: ProfilGouv): Record<BriqueGouv, Niveau> {
    const n = {} as Record<BriqueGouv, Niveau>;
    for (const b of BRIQUES) n[b.code] = p === 'administrateur' ? 'gestion' : p === 'maire' ? 'lecture' : 'aucun';
    return n;
  }
  function choisir(p: ProfilGouvDTO) {
    setSelId(p.id); setMsg(null); setRef(p.delegationArreteRef ?? '');
    const pr: ProfilGouv = p.adminComplet ? 'administrateur' : (p.profil ?? 'adjoint_delegue');
    setProfil(pr);
    if (p.adminComplet) { setNiveaux(modele('administrateur')); return; }
    const n = {} as Record<BriqueGouv, Niveau>;
    for (const b of BRIQUES) n[b.code] = p.lignes.find((l) => l.brique === b.code)?.mode ?? 'aucun';
    setNiveaux(n);
  }

  async function enregistrer() {
    if (!sel) return;
    setMsg(null);
    const briques = profil === 'administrateur' ? undefined
      : BRIQUES.filter((b) => niveaux[b.code] !== 'aucun').map((b) => ({ brique: b.code, mode: niveaux[b.code] as 'lecture' | 'gestion' }));
    try {
      await api.gouvernance.appliquerProfil(sel.id, { profil, briques, delegationArreteRef: profil === 'adjoint_delegue' ? (ref.trim() || null) : null });
      setMsg({ ok: true, t: 'Profil enregistré.' });
      await charger();
    } catch (e) { setMsg({ ok: false, t: e instanceof ApiException ? e.payload.error : 'Enregistrement impossible' }); }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-xl font-semibold text-ink">Profils et droits</h1>
        <p className="mt-1 max-w-3xl text-sm text-ink-muted">
          Le maire décide et signe ; l’administratrice ou l’administrateur complet (la DGS, ou la secrétaire de mairie)
          administre le logiciel. Chaque profil est un modèle, ajustable service par service.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
        <Card variant="glass" className="overflow-hidden p-0">
          <div className="border-b border-border px-4 py-3 text-sm font-semibold text-ink">Qui fait quoi</div>
          <ul>
            {profils.map((p) => (
              <li key={p.id}>
                <button type="button" onClick={() => choisir(p)} aria-pressed={p.id === selId}
                  className={`flex w-full items-center gap-3 border-b border-border/60 px-4 py-3 text-left ${p.id === selId ? 'bg-tech/10' : 'hover:bg-surface-hi/40'}`}>
                  <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-ink">{p.nom || '—'}</span>
                    <span className="block text-2xs text-ink-muted">{p.delegationArreteRef ? `Délégation ${p.delegationArreteRef}` : (p.service ?? '')}</span></span>
                  {badgeProfil(p)}
                </button>
              </li>
            ))}
          </ul>
          <p className="px-4 py-3 text-2xs text-ink-subtle">Il reste toujours au moins un administrateur complet.</p>
        </Card>

        {!sel ? (
          <Card variant="glass" padded><p className="text-sm text-ink-muted">Choisissez une personne pour voir ou régler son profil.</p></Card>
        ) : (
          <div className="space-y-4">
            <Card variant="glass" padded>
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-base font-semibold text-ink">{sel.nom}</h2>
                <label className="ml-auto flex items-center gap-2 text-sm"><span className="text-ink-muted">Profil</span>
                  <select className="h-10 rounded-r-md border border-border bg-surface px-2 text-sm" value={profil}
                    onChange={(e) => { const p = e.target.value as ProfilGouv; setProfil(p); setNiveaux(modele(p)); }}>
                    {(Object.keys(LIB_PROFIL) as ProfilGouv[]).map((p) => <option key={p} value={p}>{LIB_PROFIL[p]}</option>)}
                  </select>
                </label>
              </div>
              {profil !== 'administrateur' && (
                <div className="mt-4">
                  <p className="mb-2 text-xs text-ink-muted">Lecture : consulter et suivre. Gestion : saisir, instruire, modifier.</p>
                  <div role="table" aria-label="Accès par service">
                    {BRIQUES.map((b) => (
                      <div role="row" key={b.code} className="flex items-center border-b border-border/60 py-2">
                        <span role="cell" className="flex-1 text-sm text-ink">{b.libelle}</span>
                        <span role="cell" className="inline-flex overflow-hidden rounded-r-md border border-border">
                          {(['aucun', 'lecture', 'gestion'] as Niveau[]).map((n) => (
                            <button key={n} type="button" aria-pressed={niveaux[b.code] === n}
                              aria-label={`${b.libelle} : ${n}`}
                              onClick={() => setNiveaux((x) => ({ ...x, [b.code]: n }))}
                              className={`h-8 border-l border-border px-3 text-xs first:border-l-0 ${niveaux[b.code] === n
                                ? (n === 'gestion' ? 'bg-success/15 font-semibold text-success' : n === 'lecture' ? 'bg-tech/15 font-semibold text-tech-fg' : 'bg-surface-hi font-semibold text-ink')
                                : 'text-ink-muted'}`}>
                              {n === 'aucun' ? 'Aucun' : n === 'lecture' ? 'Lecture' : 'Gestion'}
                            </button>
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {profil === 'adjoint_delegue' && (
                <label className="mt-3 block text-xs text-ink">Référence de l’arrêté de délégation
                  <Input className="mt-1" value={ref} maxLength={120} placeholder="Ex : AR-2026-012" onChange={(e) => setRef(e.target.value)} />
                </label>
              )}
              {msg && <p className={`mt-3 text-xs ${msg.ok ? 'text-success' : 'text-urgent'}`} role={msg.ok ? 'status' : 'alert'}>{msg.t}</p>}
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="primary" onClick={() => void enregistrer()}>Enregistrer</Button>
                <Button size="sm" variant="ghost" onClick={() => setNiveaux(modele(profil))}>Revenir au modèle</Button>
              </div>
            </Card>
            {profil === 'maire' && (
              <Card variant="glass" padded>
                <h3 className="mb-2 text-sm font-semibold text-ink">Ce qui reste au maire, dans tous les cas</h3>
                <ul className="space-y-2 text-sm text-ink">
                  <li className="flex gap-2"><Gavel className="mt-0.5 h-4 w-4 text-tech-fg" aria-hidden="true" /> Valider et signer ce qui lui revient (arrêtés, étapes de circuit).</li>
                  <li className="flex gap-2"><Eye className="mt-0.5 h-4 w-4 text-tech-fg" aria-hidden="true" /> La vue maire consolidée, sans données personnelles détaillées.</li>
                  <li className="flex gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 text-tech-fg" aria-hidden="true" /> La gouvernance : identité de la commune, passation, déports, et son droit de reprise.</li>
                </ul>
              </Card>
            )}
          </div>
        )}
      </div>
      <RepriseMairePanel />
    </div>
  );
}
